import React, { useState, useEffect } from "react";
import { getRequest, delRequest } from "../../axiosRoutes/index";
import {
  Button,
  Box,
  CircularProgress,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Pagination,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardActions,
  useMediaQuery,
} from "@mui/material";
import AddBankDetails from "./addBankDetails";
import SideNavBar from "../sidebar/SideNav";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "../../axiosRoutes/snackbarConfig";
import { useInView } from "react-intersection-observer";

const Clients = () => {
  const { snackbar, showSnackbar, handleSnackbarClose } = useSnackbar();
  const [hover, setHover] = useState(null);
  const isSmallScreen = useMediaQuery("(max-width:900px)");
  const [bankDetails, setBankDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [updateId, setUpdateId] = useState(null);
  const [bankId, setBankId] = useState(null);
  const [openBank, setOpenBank] = useState(false);
  const [bankData, setBankData] = useState({
    title: "",
    details: "",
  });
  const [page, setPage] = useState(1)
  const [totalPages , setTotalPages] = useState(1)
  const [isFetchingBank, setIsFetchingBank] = useState(false)
  const { ref, inView } = useInView({
      threshold: 0,
  });
  const [limit, setLimit] = useState(10)
  
    useEffect(() => {
        if (inView) {
          if (limit < totalPages * 10) {
            setLimit((prev) => prev + 10);
          }
        }
      }, [inView]);

  const fetchBankDetails = async () => {
    const access = localStorage.getItem("access");
    try {
      !isSmallScreen && setLoading(true)
      setIsFetchingBank(true)
      const response = await getRequest("admin/bank/details", {
        headers: {
          Authorization: `Bearer ${access}`,
        },
        params: !isSmallScreen ? {page:page} : {limit: limit}
      });
      setIsFetchingBank(false)
      setTotalPages(response?.totals?.total_pages)
      setBankDetails(response?.data)
    } catch (error) {
      console.error("Error fetching Bank Details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankDetails();
  }, [page, limit]);

  const handleDelete = async () => {
    try {
      const access = localStorage.getItem("access");
      const response = await delRequest(`admin/bank/${deleteId}`, access);
      fetchBankDetails();
      handleClose();
      showSnackbar(response, "success");
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleOpen = () => {
    setOpenModal(true);
  };

  const handleClickOpen = (id) => {
    setDeleteId(id);
    setOpen(true);
  };

  const selectBank = (id) => {
    setUpdateId(id);
    handleOpen();
  };

  const handleBank = (id) => {
    setOpenBank(true);
    setBankId(id);
  };

  useEffect(() => {
    const fetchBank = async () => {
      if (bankId !== null) {
        const access = localStorage.getItem("access");
        try {
          setLoading(true);
          const response = await getRequest(`admin/bank/${bankId}`, {
            headers: {
              Authorization: `Bearer ${access}`,
            },
          });
          setBankData({
            title: response.title,
            details: response.details,
          });
        } catch (error) {
          console.error("Error fetching Bank Details:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setBankData({
          title: "",
          details: "",
        });
      }
    };
    fetchBank();
  }, [bankId]);

  const handleClose = () => {
    setOpenModal(false);
    setOpen(false);
    setOpenBank(false)
    fetchBankDetails();
    setBankId(null)
    setDeleteId(null);
    setUpdateId(null);
  };

  const headingTableCellStyle = {
    color: "#fff",
    textAlign: "center",
    borderBottom: "2px solid #1F1F1F",
    fontSize: { xs: "0.8rem", sm: "1rem" },
    backgroundColor: "#1F1F1F",
  };

  const dataTableCellStyle = (hover, id) => {
    return {
      color: hover === id ? "#FF5722" : "#fff",
      textAlign: "center",
      borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
      fontSize: { xs: "0.8rem", sm: "1rem" },
      backgroundColor: "#292929",
    };
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        textAlign="center"
      >
        <CircularProgress size={50} sx={{ color: "#FF5722" }} />
        <Typography variant="h6" sx={{ marginTop: "18px", color: "#fff" }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <div className="app-container" style={{ color: "#fff", display: "flex" }}>
        <SideNavBar />
        <div style={{ flex: 1, margin: `${isSmallScreen ? '2.5rem' : '0.5rem'} 0.5rem 0.5rem 0.5rem` }}>
          <Box sx={{ backgroundColor: "#000", marginTop: { xs: 1, md: 0 } }}>
            <Grid
              container
              sx={{
                alignItems: "center",
                justifyContent: { xs: "center", md: "space-between" },
                flexDirection: { xs: "column", md: "row" },
                padding: "1rem",
              }}
            >
              <Grid item>
                <h1>Bank Details</h1>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleOpen}
                  sx={{
                    backgroundColor: "#fff",
                    color: "black",
                    "&:hover": {
                      backgroundColor: "#e1e1e1",
                    },
                  }}
                >
                  Add Bank Details
                </Button>
              </Grid>
            </Grid>
            {bankDetails.length === 0 ? (
              <Typography
                variant="h6"
                sx={{ color: "#fff", textAlign: "center" }}
              >
                No Data Yet
              </Typography>
            ) : (
              <>
                {!isSmallScreen ? (
                  <TableContainer
                    sx={{ overflowX: "auto", maxWidth: "100vw", m: 1 }}
                  >
                    <Table style={{
                        borderRadius: "25px",
                        borderCollapse: "separate",
                        overflow: "hidden",
                      }}>
                      <TableHead>
                        <TableRow>
                          <TableCell style={headingTableCellStyle}>
                            ID
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Account Title
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Details
                          </TableCell>
                          <TableCell style={headingTableCellStyle}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bankDetails.map((bank, id) => (
                          <TableRow
                            key={id}
                            style={{
                              cursor: "pointer",
                            }}
                            onMouseEnter={() => setHover(id)}
                            onMouseLeave={() => setHover(null)}
                            onClick={() => handleBank(bank._id)}
                          >
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {(page - 1) * 10 + id + 1}
                            </TableCell>
                            <TableCell style={dataTableCellStyle(hover, id)}>
                              {bank.title}
                            </TableCell>
                            <TableCell
                              style={{
                                color: hover === id ? "#FF5722" : "#fff",
                                textAlign: "center",
                                display: "flex",
                                justifyContent: "center",
                                borderBottom:
                                  "1px solid rgba(255, 255, 255, 0.2)",
                                fontSize: { xs: "0.8rem", sm: "1rem" },
                                backgroundColor: "#292929",
                              }}
                            >
                              <ul style={{ textAlign: "left" }}>
                                {bank.details
                                  .split(",")
                                  .map((detail, index) => (
                                    <li key={index}>{detail.trim()}</li>
                                  ))}
                              </ul>
                            </TableCell>

                            <TableCell style={dataTableCellStyle(hover, id)}>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#1976D2",
                                  marginRight: "5px",
                                }}
                                aria-label="view"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBank(bank._id);
                                }}
                              >
                                <VisibilityIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#4CAF50",
                                  margin: "5px",
                                }}
                                aria-label="edit"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectBank(bank._id);
                                }}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#F44336",
                                  margin: "5px",
                                }}
                                aria-label="delete"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOpen(bank._id);
                                }}
                              >
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter style={{ backgroundColor: "#1F1F1F" }}>
                        <TableRow>
                          <TableCell
                            colSpan={100}
                            style={{
                              color: "white",
                              border: "none",
                              textAlign: "center",
                            }}
                          >
                            <Pagination
                              count={totalPages}
                              variant="outlined"
                              color="primary"
                              page={page}
                              onChange={(e, value) => {
                                setPage(value);
                              }}
                              sx={{
                                display: "inline-block",
                                "& .MuiPaginationItem-root": {
                                  color: "#fff",
                                  borderColor: "#fff",
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    backgroundColor: "#FF5722",
                                    color: "#fff",
                                    borderColor: "#FF5722",
                                  },
                                  "&.Mui-selected": {
                                    backgroundColor: "#FF5722",
                                    color: "#fff",
                                    borderColor: "#FF5722",
                                  },
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                ) : (
                  <Grid container spacing={1} justifyContent='center'>
                    {bankDetails.map((bank, id) => (
                      <Grid item xs={12} key={id}>
                        <Card ref = {id+1 ===bankDetails.length ? ref : null}
                          sx={{
                            backgroundColor: "#101010",
                            color: "#fff",
                          }}
                          onMouseEnter={() => setHover(id)}
                          onMouseLeave={() => setHover(null)}
                          onClick={() => handleBank(bank._id)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <CardContent>
                              <Typography variant="h6">{bank.title}</Typography>
                            </CardContent>
                            <CardActions>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#1976D2",
                                  marginRight: "1px",
                                }}
                                aria-label="view"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleBank(bank._id);
                                }}
                              >
                                <VisibilityIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#4CAF50",
                                  marginRight: "8px",
                                }}
                                aria-label="edit"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  selectBank(bank._id);
                                }}
                              >
                                <EditIcon fontSize="inherit" />
                              </IconButton>
                              <IconButton
                                style={{
                                  color: "#fff",
                                  backgroundColor: "#F44336",
                                  margin: "1px",
                                }}
                                aria-label="delete"
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClickOpen(bank._id);
                                }}
                              >
                                <DeleteIcon fontSize="inherit" />
                              </IconButton>
                            </CardActions>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                    
                  </Grid>
                )}
              </>
            )}
          </Box>

          <Dialog
            open={openBank}
            maxWidth
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#212121",
                opacity: 0.9,
                color: "#fff",
                padding: { xs: "5px", md: "15px" },
                maxWidth: { xs: "90%", md: "40%" },
                border: "2px solid #D0521B",
              },
              "& .MuiDialogContentText-root": {
                color: "#fff",
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "22px",
                fontWeight: "bold",
              }}
            >
              {bankData.title}
              <IconButton
                onClick={handleClose}
                sx={{
                  color: "#fff",
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                sx={{ borderTop: "2px solid #D0521B", paddingTop: "1rem" }}
              >
                <Grid item xs={12} md={6}>
                  <Typography variant="body1" sx={{ marginBottom: 2 }}>
                    Bank Title:
                    <span style={{ marginLeft: "1rem" }}>{bankData.title}</span>
                  </Typography>
                </Grid>
                {bankData.details.split(",").map((detail, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Typography variant="body1" sx={{ marginBottom: 2 }}>
                      {detail.trim()}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
          </Dialog>
          <Dialog
            open={open}
            sx={{
              "& .MuiPaper-root": {
                backgroundColor: "#212121",
                color: "#fff",
                padding: "15px",
              },
              "& .MuiDialogTitle-root": {
                padding: "5px",
              },
              "& .MuiDialogContentText-root": {
                color: "#fff",
              },
              "& .MuiDialogContent-root": {
                padding: "5px",
              },
              "& .MuiDialogActions-root": {
                padding: "5px",
              },
            }}
          >
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you Sure You Want to Delete this Client?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                sx={{
                  backgroundColor: "#A6A6A6",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#B8B8B8" },
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                sx={{
                  backgroundColor: "#E5622D",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#D0521B" },
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
          <AddBankDetails
            open={openModal}
            handleClose={handleClose}
            bankId={updateId}
          />
        </div>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isSmallScreen && <Grid container width='100%' height='10vh' alignItems='center' justifyContent='center' style={{position: 'fixed', bottom: (isFetchingBank ? '20px' : '-100px'), transition: 'bottom 0.5s ease'}}>
              <CircularProgress size="3rem" sx={{color: "#FF5722"}} />
            </Grid>}
    </>
  );
};

export default Clients;
